/**
 * Servicio de Autenticación
 * Monedero Digital NFC
 */

import bcrypt from 'bcryptjs';
import storageService from './storageService';
import mockDataService from './mockDataService';
import { ERROR_TYPES } from '../utils/constants';

class AuthService {
  /**
   * Inicia sesión de usuario
   */
  async login(username, password) {
    try {
      // Busca el usuario
      const user = mockDataService.getUserByUsername(username);

      if (!user) {
        throw new Error(ERROR_TYPES.INVALID_CREDENTIALS);
      }

      // Verifica que el usuario esté activo
      if (!user.activo) {
        throw new Error('Usuario desactivado');
      }

      // Verifica la contraseña
      // En desarrollo, permite contraseñas en texto plano para facilitar testing
      let isValidPassword = false;
      
      if (user.password.startsWith('$2a$') || user.password.startsWith('$2b$')) {
        // Password hasheada con bcrypt
        isValidPassword = await bcrypt.compare(password, user.password);
      } else {
        // Password en texto plano (solo desarrollo)
        isValidPassword = password === user.password;
      }

      if (!isValidPassword) {
        throw new Error(ERROR_TYPES.INVALID_CREDENTIALS);
      }

      // Crea datos de sesión (sin incluir la contraseña)
      const sessionData = {
        id: user.id,
        username: user.username,
        nombre: user.nombre,
        rol: user.rol,
        email: user.email
      };

      // Guarda la sesión
      storageService.saveSession(sessionData);

      console.log('✅ Login exitoso:', user.username);
      
      return {
        success: true,
        user: sessionData
      };
    } catch (error) {
      console.error('❌ Error en login:', error);
      
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Cierra la sesión del usuario
   */
  logout() {
    try {
      storageService.clearSession();
      console.log('✅ Logout exitoso');
      return { success: true };
    } catch (error) {
      console.error('❌ Error en logout:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Verifica si hay una sesión activa
   */
  isAuthenticated() {
    const session = storageService.getSession();
    return session !== null;
  }

  /**
   * Obtiene la sesión actual del usuario
   */
  getCurrentUser() {
    return storageService.getSession();
  }

  /**
   * Actualiza el tiempo de expiración de la sesión
   */
  refreshSession() {
    return storageService.refreshSession();
  }

  /**
   * Verifica si el usuario tiene un rol específico
   */
  hasRole(role) {
    const user = this.getCurrentUser();
    return user && user.rol === role;
  }

  /**
   * Verifica si el usuario es vendedor
   */
  isVendor() {
    return this.hasRole('vendedor');
  }

  /**
   * Verifica si el usuario es administrador
   */
  isAdmin() {
    return this.hasRole('administrador');
  }

  /**
   * Cambia la contraseña del usuario (futuro)
   */
  async changePassword(currentPassword, newPassword) {
    try {
      const user = this.getCurrentUser();
      
      if (!user) {
        throw new Error(ERROR_TYPES.SESSION_EXPIRED);
      }

      // Obtiene datos completos del usuario
      const fullUser = mockDataService.getUserByUsername(user.username);

      // Verifica contraseña actual
      const isValidPassword = await bcrypt.compare(currentPassword, fullUser.password);

      if (!isValidPassword) {
        throw new Error('Contraseña actual incorrecta');
      }

      // Hash de la nueva contraseña
      const hashedPassword = await bcrypt.hash(newPassword, 10);

      // TODO: En el futuro, actualizar en la base de datos
      console.log('Nueva contraseña hasheada:', hashedPassword);

      return {
        success: true,
        message: 'Contraseña actualizada correctamente'
      };
    } catch (error) {
      console.error('❌ Error cambiando contraseña:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Genera un hash de contraseña (para testing/admin)
   */
  async hashPassword(password) {
    return await bcrypt.hash(password, 10);
  }
}

// Exporta una instancia única (Singleton)
const authService = new AuthService();
export default authService;
